import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { SITE } from "@/constants/site";
import { Mail, Phone, MapPin, ArrowLeft } from "lucide-react";

const NAV = [
  { to: "/syarat-ketentuan", label: "Syarat & Ketentuan" },
  { to: "/kebijakan-refund", label: "Kebijakan Refund" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontak", label: "Kontak" },
];

export const PublicShell = ({ title, subtitle, children }) => {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="public-page">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#064e3b] text-white shadow-md">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <Link to="/login" className="flex items-center gap-3" data-testid="public-logo-link">
            <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain drop-shadow" />
            <div className="leading-tight">
              <p className="text-emerald-300 font-heading font-bold text-sm sm:text-base">AL-KHOIR</p>
              <p className="text-emerald-100/80 text-xs">{SITE.serviceName}</p>
            </div>
          </Link>
          <Link
            to="/login"
            data-testid="public-login-link"
            className="text-sm bg-emerald-600 hover:bg-emerald-500 transition-colors px-4 py-2 rounded-full font-semibold"
          >
            Masuk
          </Link>
        </div>
        {/* Sub nav */}
        <nav className="border-t border-emerald-900/40 bg-[#053d2e]">
          <div className="max-w-5xl mx-auto px-5 flex gap-1 overflow-x-auto">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "")}`}
                className={`whitespace-nowrap px-3 py-3 text-xs sm:text-sm border-b-2 transition-colors ${
                  pathname === n.to
                    ? "border-emerald-400 text-white font-semibold"
                    : "border-transparent text-emerald-200/80 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-10">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-900 mb-6"
          data-testid="public-back-link"
        >
          <ArrowLeft size={16} /> Kembali ke Beranda
        </Link>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-stone-900">{title}</h1>
        {subtitle && <p className="text-stone-500 mt-2">{subtitle}</p>}
        <p className="text-xs text-stone-400 mt-3">Terakhir diperbarui: Juni 2026</p>
        <div className="prose prose-stone max-w-none mt-8 text-stone-700 leading-relaxed">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#064e3b] text-emerald-100/90 mt-8">
        <div className="max-w-5xl mx-auto px-5 py-10 grid sm:grid-cols-2 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_URL} alt="" className="w-9 h-9 object-contain" />
              <p className="font-heading font-bold text-white">{SITE.name}</p>
            </div>
            <p className="text-sm text-emerald-200/80 max-w-sm">
              {SITE.serviceName} — pengelolaan pembayaran pendidikan & koperasi santri yang amanah dan mudah.
            </p>
          </div>
          <div className="text-sm space-y-2">
            <p className="font-semibold text-white mb-1">Kontak</p>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email">
              <Mail size={15} /> {SITE.email}
            </a>
            <a href={`https://wa.me/${SITE.phoneRaw}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-phone">
              <Phone size={15} /> {SITE.phone} ({SITE.waName})
            </a>
            <p className="flex items-start gap-2">
              <MapPin size={15} className="mt-0.5 shrink-0" /> <span>{SITE.address}</span>
            </p>
          </div>
        </div>
        <div className="border-t border-emerald-900/40">
          <div className="max-w-5xl mx-auto px-5 py-4 flex flex-wrap items-center justify-between gap-3 text-xs text-emerald-200/70">
            <p>© 2026 {SITE.name}. Hak cipta dilindungi.</p>
            <div className="flex gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="hover:text-white">
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
